import { Link, useParams } from 'react-router-dom'

import { StudyChart } from '../components/StudyCharts'
import type { BlogBlock, BlogPost } from '../posts'

export type PostPageProps = {
  posts: BlogPost[]
}

export const PostPage = (props: PostPageProps) => {
  const { posts } = props
  const { slug } = useParams()
  const post = posts.find((p) => p.slug === slug)

  if (!post) {
    return (
      <div className="blog-post-shell">
        <div className="blog-post blog-post__missing">
          <p>That note isn’t here.</p>
          <p>
            <Link to="/">Back to all notes</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="blog-post-shell">
      <article className="blog-post">
        <Link className="blog-post__back" to="/">
          ← All notes
        </Link>
        <time className="blog-post__date" dateTime={post.date}>
          {formatDate(post.date)}
        </time>
        <h1 className="blog-post__title">{post.title}</h1>
        {post.hero ? (
          <figure className="blog-post__hero">
            <img src={post.hero.src} alt={post.hero.alt} />
            {post.hero.caption ? (
              <figcaption className="blog-post__figcaption">{post.hero.caption}</figcaption>
            ) : null}
          </figure>
        ) : null}
        <div className="blog-post__body">
          {post.body.map((block, index) => (
            <Block key={index} block={block} />
          ))}
        </div>
      </article>
    </div>
  )
}

const Block = (props: { block: BlogBlock }) => {
  const { block } = props

  if (block.type === 'p') {
    return <p className="blog-post__paragraph">{block.text}</p>
  }

  if (block.type === 'figure') {
    const { figure } = block
    return (
      <figure className="blog-post__figure">
        <img src={figure.src} alt={figure.alt} loading="lazy" />
        {figure.caption ? (
          <figcaption className="blog-post__figcaption">{figure.caption}</figcaption>
        ) : null}
      </figure>
    )
  }

  if (block.type === 'chart') {
    return <StudyChart id={block.chart} />
  }

  return (
    <aside className="blog-post__sources" aria-label="Sources">
      <h2 className="blog-post__sources-title">Sources</h2>
      <ol className="blog-post__sources-list">
        {block.items.map((item) => (
          <li key={item.href}>
            <a href={item.href} target="_blank" rel="noreferrer">
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </aside>
  )
}

function formatDate(iso: string) {
  const date = new Date(`${iso}T12:00:00`)
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
